/**
 * Tab order of the my-tasks grid: per row the row itself, which opens the task the way the classic
 * list's row-wide link did, followed by the controls that row holds. TasksGrid.component moves the
 * focus along them - ag-grid offers the rows no stops of its own while cell focus is suppressed.
 *
 * Pure DOM functions, so the order can be unit-tested on its own.
 */

const FOCUSABLE = 'button, input, select, textarea, a[href], [tabindex]';

/** would the browser hand focus to this element when tabbing */
function isTabbable(element: HTMLElement): boolean {
    if (element.getAttribute('tabindex') === '-1') return false;
    if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') return false;
    return !element.closest('.ag-hidden');
}

export function focusableWithin(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isTabbable);
}

export function rowIndexOf(row: HTMLElement): number {
    return Number(row.getAttribute('row-index'));
}

// the rendered rows in display order: ag-grid reuses row elements as the list scrolls, so DOM
// order is not row order
export function renderedRows(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>('.ag-row'))
        .filter(row => !row.closest('.ag-hidden'))
        .sort((first, second) => rowIndexOf(first) - rowIndexOf(second));
}

/**
 * The stops one row offers: a task row is a stop itself - Enter on it opens the task - ahead of its
 * checkbox and action buttons, while a group row is only its controls, ag-grid giving a full-width
 * row no tab index. A handled task offers nothing, its row and its checkbox both being out of reach.
 */
export function rowTabStops(row: HTMLElement): HTMLElement[] {
    const inside = focusableWithin(row);
    return row.hasAttribute('tabindex') && isTabbable(row) ? [row, ...inside] : inside;
}

export function bodyTabStops(root: HTMLElement): HTMLElement[] {
    return renderedRows(root).flatMap(rowTabStops);
}

// the page's own stop next to the grid: ag-grid's tab guards would otherwise pull the focus back in
export function pageStopOutside(root: HTMLElement, backwards: boolean): HTMLElement | undefined {
    const outside = focusableWithin(root.ownerDocument.body).filter(element => !root.contains(element));
    const position = backwards ? Node.DOCUMENT_POSITION_PRECEDING : Node.DOCUMENT_POSITION_FOLLOWING;
    const candidates = backwards ? outside.reverse() : outside;
    return candidates.find(element => root.compareDocumentPosition(element) & position);
}

/**
 * The control the tab should stop on next - the select-all checkbox is one - which ag-grid steps
 * over on its way to the neighbouring header cell: the focused cell's own control when tabbing on,
 * the one in the cell before it when tabbing back.
 */
export function headerCellControl(root: HTMLElement, columns: {getColId: () => string}[],
                                  headerCell: HTMLElement, backwards: boolean): HTMLElement | undefined {
    if (!backwards) return focusableWithin(headerCell)[0];

    const position = columns.findIndex(column => column.getColId() === headerCell.getAttribute('col-id'));
    const previous = position > 0
        ? root.querySelector<HTMLElement>('.ag-header-cell[col-id="' + columns[position - 1].getColId() + '"]')
        : null;
    const controls = previous ? focusableWithin(previous) : [];
    return controls[controls.length - 1];
}
